import { useCallback, useEffect, useRef } from 'react';

/**
 * Trailing-edge debounce: each call resets the timer, so only the last call within
 * the window fires. A pending call is cancelled on unmount — callers must be safe
 * to never fire (e.g. revalidation that a remount performs anyway).
 */
export function useDebouncedCallback(callback: () => void, delayMs: number): () => void {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
  }, []);

  return useCallback(() => {
    if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      callbackRef.current();
    }, delayMs);
  }, [delayMs]);
}
